/**
 * Legacy SKU Search Client
 * Fetches add-on products by SKU using Shopify's search endpoint
 * LEGACY FALLBACK - Remove after data migration complete
 *
 * @module storefront-api
 */

/** @type {Map<string, Array>} Cache of fetched products keyed by category ID */
const cache = new Map();

/**
 * Parses a short description from product HTML description
 * Checks for ---SHORT--- delimiter first, then truncates
 * @param {string} description - Raw product description HTML
 * @returns {string|null} Short description text or null
 */
function parseShortDescription(description) {
  if (!description) return null;

  const tempDiv = document.createElement('div');

  if (description.includes('---SHORT---')) {
    const parts = description.split('---SHORT---');
    if (parts.length >= 2) {
      const shortPart = parts[1].split('---LONG---')[0];
      tempDiv.innerHTML = shortPart;
      const text = tempDiv.textContent.trim();
      return text ? truncateWords(text, 20) : null;
    }
  }

  tempDiv.innerHTML = description;
  const text = tempDiv.textContent.trim();
  return text ? truncateWords(text, 20) : null;
}

/**
 * Truncates text to a maximum number of words
 * @param {string} text - Text to truncate
 * @param {number} maxWords - Maximum word count
 * @returns {string} Truncated text with ellipsis if needed
 */
function truncateWords(text, maxWords) {
  const words = text.split(/\s+/);
  if (words.length <= maxWords) return text;
  return words.slice(0, maxWords).join(' ') + '...';
}

/**
 * Converts a price string to cents (integer)
 * Handles formatted prices with commas (e.g., "2,590.89")
 * @param {string|number} price - Price as string or number (e.g., "29.99" or "2,590.89")
 * @returns {number} Price in cents
 */
function priceToCents(price) {
  if (typeof price === 'number') return price;
  const cleaned = String(price).replace(/,/g, '');
  return Math.round(parseFloat(cleaned) * 100);
}

/**
 * Builds search URL for fetching products by SKU
 * Uses Shopify's search with variants.sku: prefix for exact matching
 * @param {Array<string>} skus - Array of variant SKUs to search for
 * @returns {string} Search URL
 */
function buildSearchUrl(skus) {
  const searchTerms = skus.map(sku => `variants.sku:${encodeURIComponent(sku)}`).join('+OR+');
  return `/search?q=${searchTerms}&view=sku`;
}

/**
 * Checks if a product has at least one variant matching the searched SKUs
 * @param {Object} product - Product object from search endpoint
 * @param {Array<string>} skus - SKUs that were searched for
 * @returns {boolean} True if product has a matching variant
 */
function productMatchesSku(product, skus) {
  if (!product.variants || !skus?.length) return false;
  return product.variants.some(variant => skus.includes(variant.sku));
}

/**
 * Transforms a product from search endpoint into drawer-compatible format
 * @param {Object} product - Product object from search endpoint
 * @returns {Array<Object>} Array of variant data objects
 */
function transformSearchProduct(product) {
  const productId = product.id;
  const productImage = product.featured_image || product.images?.[0] || null;
  const description = product.metafields?.custom?.description || parseShortDescription(product.body_html);
  const bespokeOptions = {
    dropdown: product.metafields?.bespoke?.dropdown || [],
    dimensions: product.metafields?.bespoke?.dimensions || [],
    confirmation: product.metafields?.bespoke?.confirmation || null
  };
  const preOrder = product.metafields?.custom?.pre_order || false;
  const dueDates = product.metafields?.custom?.due_dates || '';

  /**
   * Once a product is found via SKU search, include ALL its available variants
   * (not just the variant matching the SKU tag)
   */
  return product.variants
    .filter(variant => variant.available || variant.inventory_policy === 'continue')
    .map(variant => ({
      id: variant.id,
      productId,
      title: product.title,
      variantTitle: variant.title,
      price: priceToCents(variant.price),
      available: variant.available,
      inventoryQuantity: variant.inventory_quantity || 0,
      inventoryPolicy: variant.inventory_policy || 'deny',
      productImage,
      description,
      preOrder,
      dueDates,
      nextDay: false,
      bespokeOptions
    }));
}

/**
 * Fetches products by SKU using Shopify's search endpoint
 * Results are cached per category ID for subsequent calls
 * @param {string} categoryId - Category identifier for caching
 * @param {Array<string>} skus - Array of variant SKUs to fetch
 * @returns {Promise<Array<Object>>} Array of variant data in drawer-compatible format
 * @throws {Error} If request fails
 */
async function fetchProductsBySku(categoryId, skus) {
  if (cache.has(categoryId)) {
    return cache.get(categoryId);
  }

  const searchUrl = buildSearchUrl(skus);
  const response = await fetch(searchUrl);

  if (!response.ok) {
    throw new Error(`Search error: ${response.status}`);
  }

  const data = await response.json();
  const allProducts = data.products || [];

  /**
   * Filter to only products that actually have a variant matching the searched SKUs.
   * Shopify search can return fuzzy matches, so we verify the SKU exists.
   */
  const products = allProducts.filter(product => productMatchesSku(product, skus));
  const variants = products.flatMap(product => transformSearchProduct(product));

  cache.set(categoryId, variants);
  return variants;
}

/**
 * Clears the cache for a specific category or all categories
 * @param {string} [categoryId] - Category to clear, or omit for all
 */
function clearCache(categoryId) {
  if (categoryId) {
    cache.delete(categoryId);
  } else {
    cache.clear();
  }
}

export { fetchProductsBySku, clearCache };
